import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { adminApi, formatApiError, getToken, setToken, setUnauthorizedHandler } from "@/lib/adminApi";

const SiteContext = createContext(null);
const EDIT_OFF = "hausa_edit_off";
const MAX_MB = 10;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

export const imgSrc = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export const SiteProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [isAdmin, setIsAdmin] = useState(!!getToken());
  const [editing, setEditing] = useState(!!getToken() && sessionStorage.getItem(EDIT_OFF) !== "1");

  const load = useCallback(() => axios.get(`${API}/content`).then((r) => setContent(r.data)), []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load the site content"));
    setUnauthorizedHandler(() => {
      setToken(null);
      setIsAdmin(false);
      setEditing(false);
      toast.error("Your admin session expired. Please sign in again.");
    });
  }, [load]);

  const toggleEditing = (on) => {
    sessionStorage.setItem(EDIT_OFF, on ? "0" : "1");
    setEditing(on);
  };
  const logout = () => { setToken(null); setIsAdmin(false); setEditing(false); };

  const saveBlock = async (key, value) => {
    await adminApi.put("/admin/content", { blocks: { [key]: value } });
    setContent((c) => ({ ...c, blocks: { ...c.blocks, [key]: value } }));
  };
  const saveProject = async (id, patch) => {
    const { data } = await adminApi.put(`/admin/projects/${id}`, patch);
    setContent((c) => ({ ...c, projects: c.projects.map((p) => (p.id === id ? data : p)) }));
    return data;
  };
  const upload = async (file) => {
    if (!TYPES.includes(file.type)) throw new Error("Please choose a JPG, PNG or WEBP image.");
    if (file.size > MAX_MB * 1024 * 1024) throw new Error(`Images must be ${MAX_MB} MB or smaller.`);
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await adminApi.post("/admin/uploads", fd);
    return data.url;
  };

  const value = { content, isAdmin, editing, toggleEditing, logout, saveBlock, saveProject, upload };
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);
export const errMsg = (err) => err.response ? formatApiError(err.response.data?.detail) : err.message;
